/**
 * FileUploader — the "create new assumptions" entry flow.
 *
 * Despite the name it doesn't upload files from disk: step 1 is a "Create New
 * Assumptions" card; step 2 lists templates to choose from; choosing one
 * scaffolds the initial grid rows from that template's column defs (handling
 * the category_fix pre-population) and hands them to the grid via onDataLoaded.
 *
 * Ported to TSX + Nordic; framer-motion removed.
 */
import { useState } from "react";
import {
  AlertCircle,
  ChevronRight,
  LayoutTemplate,
  Loader2,
  PenLine,
} from "lucide-react";

import { cn } from "../lib/cn";
import { fetchTemplateContent, listTemplates } from "./api";
import type { GridRow, TemplateColumn, TemplateConfig, TemplateItem } from "./types";

export interface FileUploaderProps {
  onDataLoaded: (
    rows: GridRow[],
    name: string,
    templateConfig?: TemplateConfig | null,
  ) => void;
}

type Step = "initial" | "selectTemplate";

export default function FileUploader({ onDataLoaded }: FileUploaderProps) {
  const [step, setStep] = useState<Step>("initial");
  const [availableTemplates, setAvailableTemplates] = useState<TemplateItem[]>(
    [],
  );
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [loadingTemplateId, setLoadingTemplateId] = useState<string | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const handleStartNewAssumption = async () => {
    setError(null);
    setIsLoadingTemplates(true);
    setStep("selectTemplate");
    try {
      const result = await listTemplates();
      const mapped: TemplateItem[] = (result.files || []).map((f) => ({
        id: f.s3Key || f.fileId,
        name: f.fileName,
        displayName: f.fileName
          .replace("template_", "")
          .replace(/_/g, " ")
          .replace(".json", ""),
      }));
      setAvailableTemplates(mapped);
    } catch (err) {
      setError(
        "Failed to load templates: " +
          (err instanceof Error ? err.message : String(err)),
      );
      setStep("initial");
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const handleSelectTemplate = async (template: TemplateItem) => {
    setError(null);
    setLoadingTemplateId(template.id);
    try {
      const result = await fetchTemplateContent(template.id);
      const templateData = result.data;

      if (!templateData || !templateData.columns) {
        throw new Error("Invalid template format: missing 'columns' key");
      }

      const columns = templateData.columns;
      const columnNames = columns.map((c) => c.name);
      const categoryFixCol = columns.find((c) => c.type === "category_fix");

      let initialRows: GridRow[] = [];
      if (categoryFixCol) {
        const fixedOptions = (categoryFixCol.options as string[]) || [];
        initialRows = fixedOptions.map((optionValue) => {
          const row: GridRow = {};
          columnNames.forEach((colName) => {
            if (colName === categoryFixCol.name) {
              row[colName] = optionValue;
            } else {
              const colDef = columns.find((c) => c.name === colName);
              row[colName] = colDef?.default || "";
            }
          });
          return row;
        });
      } else {
        const emptyRow: GridRow = {};
        columnNames.forEach((colName) => {
          const colDef = columns.find((c) => c.name === colName);
          emptyRow[colName] = colDef?.default || "";
        });
        initialRows = [emptyRow];
      }

      const templateId = template.name.replace(".json", "");
      const templateConfig: TemplateConfig = {
        template_name: templateData.template_name,
        template_id: templateId,
        s3_key: template.id,
        columns: templateData.columns as TemplateColumn[],
      };

      onDataLoaded(initialRows, "New Assumptions", templateConfig);
    } catch (err) {
      setError(
        "Failed to load template: " +
          (err instanceof Error ? err.message : String(err)),
      );
    } finally {
      setLoadingTemplateId(null);
    }
  };

  const handleBack = () => {
    setStep("initial");
    setError(null);
  };

  return (
    <div className="w-full max-w-3xl mx-auto space-y-6">
      {step === "initial" && (
        <div className="grid grid-cols-1 gap-4 view-in">
          <button
            onClick={handleStartNewAssumption}
            className="relative rounded-lg p-10 text-center t-base group focus-ring"
            style={{
              background: "var(--bg-elev)",
              border: "1.5px dashed var(--line)",
            }}
          >
            <div className="flex flex-col items-center justify-center space-y-4">
              <div
                className="p-4 rounded-full t-base"
                style={{ background: "var(--hover)", color: "var(--ink-3)" }}
              >
                <PenLine size={32} strokeWidth={1.5} />
              </div>
              <div className="space-y-2">
                <h3
                  className="font-serif text-[18px] leading-tight"
                  style={{ color: "var(--ink)" }}
                >
                  Create New Assumptions
                </h3>
                <p
                  className="text-[13px] font-serif max-w-xs mx-auto leading-snug"
                  style={{ color: "var(--ink-3)" }}
                >
                  Select a template to start building your assumptions dataset.
                </p>
              </div>
            </div>
          </button>
        </div>
      )}

      {step === "selectTemplate" && (
        <div className="space-y-4 view-in">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="px-3 py-1.5 text-[12px] font-mono uppercase tracking-[0.12em] rounded t-base hover:bg-[var(--hover)] focus-ring"
              style={{ color: "var(--ink-2)" }}
            >
              ← Back
            </button>
            <div>
              <h3
                className="font-serif text-[18px] leading-tight"
                style={{ color: "var(--ink)" }}
              >
                Select a Template
              </h3>
              <p
                className="text-[13px] font-serif"
                style={{ color: "var(--ink-3)" }}
              >
                Choose a template to define the columns and data types for your
                assumptions.
              </p>
            </div>
          </div>

          {isLoadingTemplates ? (
            <div className="flex items-center justify-center py-12">
              <Loader2
                size={28}
                strokeWidth={1.5}
                className="animate-spin"
                style={{ color: "var(--accent)" }}
              />
              <span
                className="ml-3 font-serif text-[14px]"
                style={{ color: "var(--ink-3)" }}
              >
                Loading templates...
              </span>
            </div>
          ) : availableTemplates.length === 0 ? (
            <div className="text-center py-12" style={{ color: "var(--ink-3)" }}>
              <LayoutTemplate
                size={40}
                strokeWidth={1.25}
                className="mx-auto mb-3"
                style={{ color: "var(--ink-4)" }}
              />
              <p className="font-serif text-[14px]">No templates available.</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {availableTemplates.map((template) => (
                <button
                  key={template.id}
                  onClick={() =>
                    !loadingTemplateId && handleSelectTemplate(template)
                  }
                  disabled={!!loadingTemplateId}
                  className={cn(
                    "text-left p-4 rounded-lg border hairline t-base group focus-ring",
                    loadingTemplateId === template.id
                      ? ""
                      : loadingTemplateId
                        ? "opacity-60 cursor-default"
                        : "hover:shadow-sm",
                  )}
                  style={{
                    background: "var(--bg-elev)",
                    borderColor:
                      loadingTemplateId === template.id
                        ? "var(--accent)"
                        : undefined,
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div
                        className="p-3 rounded-lg t-base"
                        style={{
                          background: "var(--accent-soft)",
                          color: "var(--accent)",
                        }}
                      >
                        <LayoutTemplate size={20} strokeWidth={1.5} />
                      </div>
                      <h4
                        className="font-serif text-[16px] leading-tight"
                        style={{ color: "var(--ink)" }}
                      >
                        {template.displayName}
                      </h4>
                    </div>
                    {loadingTemplateId === template.id ? (
                      <Loader2
                        size={18}
                        strokeWidth={1.5}
                        className="animate-spin"
                        style={{ color: "var(--accent)" }}
                      />
                    ) : (
                      <ChevronRight
                        size={18}
                        strokeWidth={1.5}
                        className="t-base group-hover:translate-x-1"
                        style={{ color: "var(--ink-3)" }}
                      />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {error && (
        <div
          className="p-4 rounded-lg border flex items-center gap-3 view-in"
          style={{
            background: "var(--neg-soft, rgba(156,90,82,0.1))",
            borderColor: "var(--neg)",
            color: "var(--neg)",
          }}
        >
          <AlertCircle size={20} strokeWidth={1.5} className="flex-shrink-0" />
          <p className="text-[13px] font-serif font-medium">{error}</p>
        </div>
      )}
    </div>
  );
}
